import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Modal } from 'react-bootstrap';

import { useTranslate } from 'hooks/useTranslate';
import { setModal } from 'modules/_shared/redux/modalActions';
import Button from 'modules/_shared/components/Buttons';

const HowItWorksModal: FC<{ size?: string }> = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const { societyId } = useParams<{ societyId: string }>();
  const history = useHistory();

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const goToCreateSellOffer = () => {
    handleClose();
    history.push(`/crear-oferta-venta/${societyId}`);
  };

  const goToViewSellOffers = () => {
    handleClose();
    history.push(`/ofertas-venta/${societyId}`);
  };

  const goToCreateBuyOffer = () => {
    handleClose();
    history.push(`/crear-oferta-compra/${societyId}`);
  };

  return (
    <>
      <Modal.Header className="border-bottom">
        <Modal.Title className="mb-0">{t('HowItWorksModalTitle')}</Modal.Title>
        <a
          className="close cursor-pointer"
          onClick={handleClose}
          aria-label={t('Close')}
        >
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4">{t('HowItWorksModalLead')}</p>

        <h6 className="overline-title text-success mb-3">
          {t('HowItWorksModalSellSectionTitle')}
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-success-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-offer text-success" />
            </div>
            <div>
              <strong>{t('HowItWorksModalSellStep1Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalSellStep1Description')}
              </p>
              <Button
                variant="link"
                className="link link-success mt-2 btn btn-link p-0 text-start"
                onClick={goToCreateSellOffer}
              >
                {t('HowItWorksModalConfigureSellOffer')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-success-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-users text-success" />
            </div>
            <div>
              <strong>{t('HowItWorksModalSellStep2Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalSellStep2Description')}
              </p>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-success-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-check-circle text-success" />
            </div>
            <div>
              <strong>{t('HowItWorksModalSellStep3Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalSellStep3Description')}
              </p>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-success-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-swap text-success" />
            </div>
            <div>
              <strong>{t('HowItWorksModalSellStep4Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalSellStep4Description')}
              </p>
            </div>
          </li>
        </ul>

        <h6 className="overline-title text-primary mb-3">
          {t('HowItWorksModalBuySectionTitle')}
        </h6>
        <p className="text-soft mb-3 small">{t('HowItWorksModalBuyIntro')}</p>
        <ul className="list-group list-group-flush">
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-primary-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-cart text-primary" />
            </div>
            <div>
              <strong>{t('HowItWorksModalBuyOption1Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalBuyOption1Description')}
              </p>
              <Button
                variant="link"
                className="link link-primary mt-2 btn btn-link p-0 text-start"
                onClick={goToViewSellOffers}
              >
                {t('HowItWorksModalViewSellOffers')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
          <li className="list-group-item d-flex align-items-start px-0 border-0">
            <div className="icon-circle bg-primary-dim mr-3 flex-shrink-0">
              <em className="icon ni ni-offer text-primary" />
            </div>
            <div>
              <strong>{t('HowItWorksModalBuyOption2Title')}</strong>
              <p className="text-soft mb-0 small">
                {t('HowItWorksModalBuyOption2Description')}
              </p>
              <Button
                variant="link"
                className="link link-primary mt-2 btn btn-link p-0 text-start"
                onClick={goToCreateBuyOffer}
                disabled
              >
                {t('HowItWorksModalCreateBuyOffer')}{' '}
                <em className="icon ni ni-arrow-long-right ml-1" />
              </Button>
            </div>
          </li>
        </ul>
      </Modal.Body>
      <Modal.Footer className="border-top bg-lighter">
        <span className="text-soft fs-12px me-auto">
          {t('HowItWorksModalFooterHelp')}
        </span>
        <button type="button" className="btn btn-primary" onClick={handleClose}>
          {t('HowItWorksModalUnderstood')}
        </button>
      </Modal.Footer>
    </>
  );
};

export default HowItWorksModal;
